import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

export interface CreateButtonProps extends Omit<ButtonProps, 'onClick'> {
  handleClick: () => void;
  /**
   * Optional text display inside the action button.
   * @default "Nuovo"
   */
  label?: string;
}

/**
 * A highly specific, styled business action button deployed to initiate a new equipment checklist workflow.
 * Encapsulates dedicated padding, custom tracking colors, and cross-browser elevation layouts.
 *
 * @component
 * @example
 * ```tsx
 * <CreateButton handleClick={(payload) => handleNavigate(payload)} />
 * ```
 */
export const CreateButton = ({
  handleClick,
  label = 'Nuovo',
  disabled,
  sx,
  ...rest
}: CreateButtonProps) => {
  return (
    <Button
      {...rest}
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
      disableElevation
      disabled={disabled}
      onClick={handleClick}
      sx={{
        textTransform: 'none',
        backgroundColor: '#1d70b8',
        fontWeight: 600,
        fontSize: '0.875rem',
        borderRadius: 2,
        px: 2.5,
        height: '40px',
        boxShadow: 'none',
        whiteSpace: 'nowrap',
        minWidth: 'max-content',
        '& .MuiButton-startIcon': {
          color: '#ffffff',
          marginRight: '6px'
        },
        '&:hover': {
          boxShadow: 'none',
          backgroundColor: '#155a96',
          textDecoration: 'none'
        },
        ...sx
      }}>
      {label}
    </Button>
  );
};
